import { useState } from 'react'
import { Confirm, Loading, Notify } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../../helpers/apiRequester'
import styles from '../shared/mhlShared.module.scss'
import { Pill } from '../shared/SourceTabs'
import type { MhlCampaign, MhlClient, MhlLeadRecord } from '../shared/mhlTypes'
import { formatDateTime, reviewLabel, reviewTone, splitPersonLabel } from '../shared/mhlFormatting'
import { colors, InfoTip } from '../../../../ui'

interface Props {
    lead: MhlLeadRecord
    client: MhlClient
    campaign: MhlCampaign | null
    hasAudiogram: boolean
    /** True once the audiogram has been assessed as above / below 4 dB. */
    isAssessed: boolean
    canReview: boolean
    /** Admins / named users only: may approve a client who was rejected. */
    canOverturn: boolean
    onChanged: () => Promise<void> | void
}

/**
 * Approval gate for dashboard-submitted clients. Approving releases the e-sign
 * link through the ticked channels; rejecting records a reason and blocks the
 * link for this lead. A rejection can later be overturned (approved) by an
 * admin or a named overturn user — mirrored server-side.
 */
export const ReviewPanel = ({ lead, client, campaign, hasAudiogram, isAssessed, canReview, canOverturn, onChanged }: Props) => {
    const [notes, setNotes] = useState('')
    const [sendEmail, setSendEmail] = useState(!!client.email)
    const [sendSms, setSendSms] = useState(false)
    const [busy, setBusy] = useState(false)
    // The overturn controls stay hidden behind a button so a rejected client's
    // panel reads as read-only until someone explicitly opens the flow.
    const [overturnOpen, setOverturnOpen] = useState(false)

    const status = lead.review_status
    const awaiting = status === 'awaiting_review'
    const mobile = client.mobile_number || client.telephone
    const reviewedBy = splitPersonLabel(lead.reviewed_by)
    // A decision requires evidence on file: the audiogram itself plus a recorded
    // above / below 4 dB assessment. Mirrors the server-side gate.
    const blockers: string[] = []
    if (!hasAudiogram) blockers.push('an audiogram must be uploaded')
    if (!isAssessed) blockers.push('the audiogram must be assessed as above or below 4 dB')
    const decisionReady = blockers.length === 0

    const submitReview = async (decision: 'approve' | 'reject', overturn = false) => {
        setBusy(true)
        Loading.circle(overturn ? 'Overturning rejection…' : decision === 'approve' ? 'Approving client…' : 'Recording rejection…')
        try {
            const sendVia = decision === 'approve' ? [...(sendEmail ? ['email'] : []), ...(sendSms ? ['sms'] : [])] : []
            const res = await api({
                url: '/military-hearing-loss-lead-review',
                data: { case_ref: lead.case_ref, decision, notes: notes.trim() || undefined, sendVia, ...(overturn ? { overturn: true } : {}) },
            })
            if (!res.data?.success) {
                Report.failure('Review failed', res.data?.message || 'Could not record the review', 'Okay')
                return
            }
            if (decision === 'reject') {
                Notify.success('Client rejected')
            } else {
                const results = res.data.sendResults || {}
                const failures = Object.entries(results)
                    .filter(([, r]: [string, any]) => r && !r.success)
                    .map(([channel, r]: [string, any]) => `${channel.toUpperCase()}: ${r.message || 'failed'}`)
                if (failures.length) {
                    Report.warning(
                        'Approved — link not fully sent',
                        `The client is approved but sending failed for:\n\n${failures.join('\n')}\n\nYou can re-send from the E-sign section.`,
                        'Okay',
                    )
                } else if (sendVia.length) {
                    Notify.success(`Approved · e-sign link sent via ${sendVia.map((c) => c.toUpperCase()).join(' & ')}`)
                } else {
                    Notify.success('Approved · no link sent yet')
                }
            }
            setNotes('')
            await onChanged()
        } catch (err: any) {
            Report.failure('Review failed', err?.response?.data?.message || err?.message || 'Could not record the review', 'Okay')
        } finally {
            Loading.remove()
            setBusy(false)
        }
    }

    const confirmApprove = () => {
        const channels = [...(sendEmail ? ['email'] : []), ...(sendSms ? ['SMS'] : [])]
        Confirm.show(
            'Approve this client?',
            channels.length
                ? `The CFA e-sign link will be sent to the client via ${channels.join(' and ')}.`
                : 'The client will be approved. No link will be sent until you send it from the E-sign section.',
            'Approve',
            'Cancel',
            () => void submitReview('approve'),
            () => {},
            { okButtonBackground: colors.good, titleColor: colors.ink },
        )
    }

    const confirmReject = () => {
        if (!notes.trim()) {
            Report.warning('Reason required', 'Please enter a short reason before rejecting the client.', 'Okay')
            return
        }
        Confirm.show(
            'Reject this client?',
            'The client will be marked as rejected and the e-sign link will not be sent.',
            'Reject',
            'Cancel',
            () => void submitReview('reject'),
            () => {},
            { okButtonBackground: colors.bad, titleColor: colors.ink },
        )
    }

    const confirmOverturn = () => {
        if (!notes.trim()) {
            Report.warning('Reason required', 'Please enter a short reason for overturning the rejection.', 'Okay')
            return
        }
        const channels = [...(sendEmail ? ['email'] : []), ...(sendSms ? ['SMS'] : [])]
        Confirm.show(
            'Overturn rejection and approve?',
            channels.length
                ? `The rejection will be overturned, the client approved, and the CFA e-sign link sent via ${channels.join(' and ')}.`
                : 'The rejection will be overturned and the client approved. No link will be sent until you send it from the E-sign section.',
            'Overturn & approve',
            'Cancel',
            () => void submitReview('approve', true),
            () => {},
            { okButtonBackground: colors.good, titleColor: colors.ink },
        )
    }

    return (
        <section className={styles.panel}>
            <h3 className={styles.panelTitle}>Review &amp; approval</h3>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                <Pill tone={reviewTone(status)} dot>
                    {reviewLabel(status)}
                </Pill>
                {lead.reviewed_at && <span className={styles.metaText}>{formatDateTime(lead.reviewed_at)}</span>}
            </div>

            {lead.reviewed_by && (
                <dl className={`${styles.defList} ${styles.defListTight}`} style={{ marginBottom: 12 }}>
                    <dt>Reviewed by</dt>
                    <dd>
                        {reviewedBy.name}
                        {reviewedBy.email && <InfoTip text={reviewedBy.email} className={styles.emailTip} />}
                    </dd>
                    {lead.review_notes && (
                        <>
                            <dt>Notes</dt>
                            <dd>{lead.review_notes}</dd>
                        </>
                    )}
                </dl>
            )}

            {awaiting && !canReview && (
                <p className={styles.hint}>
                    This client is waiting for the head of Military Hearing Loss to review the file. The e-sign link will be sent once approved.
                </p>
            )}

            {awaiting && canReview && (
                <>
                    {!decisionReady ? (
                        <div className={styles.infoBanner} style={{ marginBottom: 12 }}>
                            <strong>Not ready for a decision.</strong> Before this client can be approved or rejected, {blockers.join(' and ')}. Use the Audiogram
                            section above.
                        </div>
                    ) : (
                        <p className={styles.hint} style={{ marginBottom: 10 }}>
                            Audiogram on file and assessed as <strong>{lead.audiology === '>=4dB' ? 'above 4 dB NIHL' : 'below 4 dB NIHL'}</strong>. Check the client
                            details, then approve to release the CFA e-sign link or reject with a reason.
                        </p>
                    )}

                    <div className={styles.subTitle}>Send e-sign link on approval</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                        <label className={styles.checkboxRow}>
                            <input type="checkbox" checked={sendEmail} disabled={!client.email || busy} onChange={(e) => setSendEmail(e.target.checked)} />
                            Email · {client.email || 'no email on record'}
                        </label>
                        <label className={styles.checkboxRow}>
                            <input type="checkbox" checked={sendSms} disabled={!mobile || busy} onChange={(e) => setSendSms(e.target.checked)} />
                            SMS · {mobile || 'no mobile on record'}
                        </label>
                    </div>

                    <div className={styles.subTitle}>Notes {`(required to reject)`}</div>
                    <textarea
                        className={styles.textarea}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="e.g. Audiogram shows less than 4 dB NIHL; does not meet criteria."
                        disabled={busy}
                    />

                    <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                        <button
                            type="button"
                            className={styles.btnSuccess}
                            onClick={confirmApprove}
                            disabled={busy || !decisionReady}
                            title={decisionReady ? undefined : 'Upload and assess the audiogram first'}
                        >
                            Approve client
                        </button>
                        <button
                            type="button"
                            className={styles.btnDanger}
                            onClick={confirmReject}
                            disabled={busy || !decisionReady}
                            title={decisionReady ? undefined : 'Upload and assess the audiogram first'}
                        >
                            Reject
                        </button>
                    </div>
                </>
            )}

            {status === 'approved' && campaign && !campaign.link_sent_at && (
                <p className={styles.hint} style={{ marginTop: 8 }}>
                    Approved but the e-sign link has not been sent yet — use the E-sign section to send it.
                </p>
            )}

            {status === 'rejected' && !canOverturn && <p className={styles.hint}>The e-sign link cannot be sent for a rejected client.</p>}

            {status === 'rejected' && canOverturn && (
                <>
                    <p className={styles.hint} style={{ marginBottom: 10 }}>The e-sign link cannot be sent for a rejected client.</p>

                    {!overturnOpen ? (
                        <>
                            <div className={styles.subTitle}>Change this decision</div>
                            <p className={styles.hint} style={{ marginBottom: 8, fontSize: 11.5 }}>
                                You can overturn the decision and approve this client. The original rejection stays on record.
                            </p>
                            <button type="button" className={styles.btnSmall} onClick={() => setOverturnOpen(true)} disabled={busy}>
                                Overturn rejection
                            </button>
                        </>
                    ) : (
                        <>
                            <p className={styles.hint} style={{ marginBottom: 10 }}>
                                Overturning approves the client and allows the e-sign link to be sent. The original rejection stays on record.
                            </p>

                            <div className={styles.subTitle}>Send e-sign link on approval</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                                <label className={styles.checkboxRow}>
                                    <input type="checkbox" checked={sendEmail} disabled={!client.email || busy} onChange={(e) => setSendEmail(e.target.checked)} />
                                    Email · {client.email || 'no email on record'}
                                </label>
                                <label className={styles.checkboxRow}>
                                    <input type="checkbox" checked={sendSms} disabled={!mobile || busy} onChange={(e) => setSendSms(e.target.checked)} />
                                    SMS · {mobile || 'no mobile on record'}
                                </label>
                            </div>

                            <div className={styles.subTitle}>Reason for overturning {`(required)`}</div>
                            <textarea
                                className={styles.textarea}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="e.g. Second review of the audiogram confirms more than 4 dB NIHL."
                                disabled={busy}
                            />

                            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                                <button type="button" className={styles.btnSuccess} onClick={confirmOverturn} disabled={busy}>
                                    Overturn rejection &amp; approve
                                </button>
                                <button type="button" className={styles.btnGhost} onClick={() => setOverturnOpen(false)} disabled={busy}>
                                    Cancel
                                </button>
                            </div>
                        </>
                    )}
                </>
            )}
        </section>
    )
}
